import { useEffect, useRef, useState } from "react";
import { projectAkyoNavigation } from "./akyo-taxonomy";
export type AkyoChoice = {
  chapterIds: string[];
  skillIds: string[];
  count: number;
};
type Chapter = {
  id: string;
  label: string;
  levelId: string;
  subjectId: string;
  skills: { id: string; label: string }[];
};
export default function AkyoSelection({
  onChange,
  schoolLevel,
}: {
  onChange: (value: AkyoChoice | null) => void;
  schoolLevel: string;
}) {
  const [chapters, setChapters] = useState<Chapter[]>([]),
    [ids, setIds] = useState<string[]>([]),
    [excluded, setExcluded] = useState<string[]>([]),
    [count, setCount] = useState(10),
    [stock, setStock] = useState<number | null>(null),
    [error, setError] = useState(""),
    [referenceError, setReferenceError] = useState(""),
    [referenceLoading, setReferenceLoading] = useState(true),
    [scopeLabel, setScopeLabel] = useState("");
  const revision = useRef(0);
  const invalidate = () => { revision.current += 1; onChange(null); };
  const selected = chapters.filter((c) => ids.includes(c.id));
  const skills = [
    ...new Set(selected.flatMap((c) => c.skills.map((s) => s.id))),
  ];
  const skillIds = skills.filter((id) => !excluded.includes(id));
  useEffect(() => {
    let active = true;
    setChapters([]); setIds([]); setExcluded([]); setStock(null); setScopeLabel(""); setReferenceError(""); setReferenceLoading(true);
    invalidate();
    if (!schoolLevel) { setReferenceError("Le niveau de cette classe n’est pas renseigné. Complète sa fiche avant de choisir des chapitres."); setReferenceLoading(false); return; }
    fetch("/api/qcm/akyo/reference", { credentials: "include" })
      .then(async (r) => {
        const b = await r.json();
        if (!r.ok) throw Error(b.error);
        const navigation = projectAkyoNavigation(b.taxonomy);
        const paths = navigation.flatMap(level => level.subjects
          .filter(subject => subject.schoolLevel === schoolLevel)
          .map(subject => ({levelId: level.id, subjectId: subject.id, label: `${level.label} · ${subject.label}`})));
        if (paths.length !== 1) throw Error(`Le niveau « ${schoolLevel} » n’a pas de correspondance Akyo unique. Vérifie le niveau de la classe et le référentiel.`);
        if (active) setScopeLabel(paths[0].label);
        if (active) setChapters(b.chapters.filter((chapter: Chapter) => paths.some(path =>
          path.levelId === chapter.levelId && path.subjectId === chapter.subjectId)));
      })
      .catch((e) => {
        if (active) setReferenceError(e.message);
      }).finally(() => { if(active) setReferenceLoading(false); });
    return () => {
      active = false;
    };
  }, [schoolLevel]);
  const key = JSON.stringify({ chapterIds: ids, skillIds, count });
  useEffect(() => {
    let active = true;
    const requestRevision = revision.current;
    setStock(null);
    onChange(null);
    if (
      !ids.length ||
      !skillIds.length ||
      !Number.isInteger(count) ||
      count < 1 ||
      count > 20
    )
      return;
    setError("");
    const choice = JSON.parse(key) as AkyoChoice;
    fetch("/api/qcm/akyo/available", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: key,
    })
      .then(async (r) => {
        const b = await r.json();
        if (!r.ok) throw Error(b.error);
        if (active && requestRevision === revision.current) {
          setStock(b.available);
          onChange(b.available >= count ? choice : null);
        }
      })
      .catch((e) => {
        if (active && requestRevision === revision.current) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [key]);
  return (
    <section className="akyo-selection" aria-label="Référentiel Akyo">
      <h3>Chapitres et compétences Akyo</h3>
      <p>{scopeLabel || `Classe : ${schoolLevel || "niveau non renseigné"}`}</p>
      {referenceLoading && <p role="status">Chargement des chapitres de la classe…</p>}
      {referenceError && <p role="alert">{referenceError}</p>}
      {!referenceLoading && !referenceError && !chapters.length && <p>Aucun chapitre actif pour ce niveau et cette matière.</p>}
      {chapters.map((c) => (
        <label key={c.id} style={{ display: "block" }}>
          <input
            type="checkbox"
            checked={ids.includes(c.id)}
            onChange={(e) => {
              invalidate();
              setExcluded([]);
              setIds((v) =>
                e.target.checked ? [...v, c.id] : v.filter((id) => id !== c.id),
              );
            }}
          />
          {c.label}
        </label>
      ))}
      <p>
        <button
          type="button"
          disabled={!excluded.length}
          onClick={() => {
            invalidate();
            setExcluded([]);
          }}
        >
          Tout cocher
        </button>{" "}
        <button
          type="button"
          disabled={!skills.length || excluded.length === skills.length}
          onClick={() => {
            invalidate();
            setExcluded(skills);
          }}
        >
          Tout décocher
        </button>
      </p>
      {selected.map((c) => (
        <fieldset key={c.id}>
          <legend>{c.label}</legend>
          {c.skills.map((s) => (
            <label key={s.id} style={{ display: "block" }}>
              <input
                type="checkbox"
                checked={!excluded.includes(s.id)}
                onChange={(e) => {
                  invalidate();
                  setExcluded((v) =>
                    e.target.checked
                      ? v.filter((id) => id !== s.id)
                      : [...v, s.id],
                  );
                }}
              />
              {s.label}
            </label>
          ))}
        </fieldset>
      ))}
      <label>
        Nombre de questions{" "}
        <input
          type="number"
          min={1}
          max={20}
          value={count}
          onChange={(e) => {
            if (Number(e.target.value) === count) return;
            invalidate();
            setCount(Number(e.target.value));
          }}
        />
      </label>
      <p role="status">
        {!skillIds.length
          ? "Coche au moins une compétence."
          : stock === null
            ? "Vérification du stock…"
            : `${stock} question(s) disponible(s), une variante par famille publiée.`}
      </p>
      {stock !== null && stock < count && (
        <p role="alert">
          Stock insuffisant : {stock} disponible(s) pour {count} demandée(s).
        </p>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
